import { useMemo, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { ContentLayoutF, H1, Separator } from '@betterment/samba-react';
import { ErrorBoundary } from '@betterment/shared-components';

import { AccountsCard } from './components/accounts-card/AccountsCard';
import { Balance } from './components/balance/Balance';
import { DividendsAndInterestCard } from './components/dividends-and-interest-card/DividendsAndInterestCard';
import { TaxSavingsCard } from './components/tax-savings-card/TaxSavingsCard';
import { PerformanceAccountsDocument } from './graphql/performanceAccounts.generated';

import { DateRangeOption } from '../../graphql/schema';
import { ApplicationLayout } from '../../Shared/layout/ApplicationLayout';
import { DateRangeFilter } from '../shared/date-range-filter/DateRangeFilter';
import { CustomDateRange } from '../shared/date-range-filter/dateUtils';

export const UserPerformancePage: React.FC = () => {
  const [dateRangeOption, setDateRangeOption] = useState<DateRangeOption>(DateRangeOption.AllTime);
  const [customDateRange, setCustomDateRange] = useState<CustomDateRange>();

  const handleDateRangeChange = (option: DateRangeOption, range?: CustomDateRange) => {
    setDateRangeOption(option);
    setCustomDateRange(range);
  };

  const { data, previousData } = useQuery(PerformanceAccountsDocument, {
    variables: { dateRangeOption },
  });

  const availableData = useMemo(() => data ?? previousData, [data, previousData]);

  return (
    <ErrorBoundary fallbackType="full" feature="performance_page" owner="investing_experience">
      <ApplicationLayout pageTitle="Performance">
        <ApplicationLayout.Header>
          <H1 variant="title400" css={{ paddingY: 'space.300' }}>
            Performance
          </H1>
          <Separator />
        </ApplicationLayout.Header>
        <ApplicationLayout.StickyHeader>
          <DateRangeFilter
            onChange={handleDateRangeChange}
            selected={dateRangeOption}
            customDateRange={customDateRange}
          />
        </ApplicationLayout.StickyHeader>
        <ApplicationLayout.Content>
          <ContentLayoutF>
            <ContentLayoutF.SlotContainer>
              <ContentLayoutF.Slot1
                css={{ display: 'grid', gap: { base: 'space.100', sm: 'space.300' }, gridAutoRows: 'max-content' }}
              >
                {availableData && (
                  <>
                    <Balance
                      dateRangeOption={dateRangeOption}
                      accounts={availableData.performance.accounts}
                      customDateRange={customDateRange}
                    />
                    <AccountsCard
                      dateRangeOption={dateRangeOption}
                      accounts={availableData.performance.accounts}
                      customDateRange={customDateRange}
                    />
                  </>
                )}
              </ContentLayoutF.Slot1>
              <ContentLayoutF.Slot2
                css={{
                  display: 'grid',
                  gap: { base: 'space.100', sm: 'space.300' },
                  gridAutoRows: 'max-content',
                  position: 'sticky',
                  top: '115px',
                  alignSelf: 'start',
                }}
              >
                <DividendsAndInterestCard dateRangeOption={dateRangeOption} customDateRange={customDateRange} />
                <TaxSavingsCard dateRangeOption={dateRangeOption} customDateRange={customDateRange} />
              </ContentLayoutF.Slot2>
            </ContentLayoutF.SlotContainer>
          </ContentLayoutF>
        </ApplicationLayout.Content>
      </ApplicationLayout>
    </ErrorBoundary>
  );
};
